import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiAlertTriangle, FiCheckCircle, FiChevronLeft, FiChevronRight, FiSearch, FiLink } from 'react-icons/fi';
import { AdminSidebar } from '../../components/admin';
import { Modal } from '../../components/ui';

const PAGE_SIZE = 5;
const API_BASE = 'https://www.s2i.com.tn';

const getFullImageUrl = (path, name) => {
  if (!path) return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Client')}&background=f1f5f9&color=d90000&size=128&bold=true`;
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${cleanPath}`;
};

const handleImageError = (e, name) => {
  e.target.onerror = null; 
  e.target.src = `https://via.placeholder.com/150/f1f5f9/d90000?text=${encodeURIComponent(name || 'Client')}`;
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // ✅ État mis à jour pour correspondre au DTO Java (logo_url, website_url)
  const [form, setForm] = useState({ name: '', description: '', logo_url: '', website_url: '' });
  
  const [notification, setNotification] = useState({ show: false, type: '', msg: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const token = localStorage.getItem('token');

  const fetchClients = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const res = await fetch(`${API_BASE}/api/clients?page=${page}&size=${PAGE_SIZE}&sortBy=id&direction=asc${searchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(data.content || []);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || 0);
    } catch {
      showNotify('error', 'Erreur de connexion au serveur');
    }
    setLoading(false);
  }, [searchTerm, token]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchClients(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, fetchClients]);

  useEffect(() => {
    fetchClients(currentPage - 1);
  }, [currentPage, fetchClients]);

  const showNotify = (type, msg) => {
    setNotification({ show: true, type, msg });
    setTimeout(() => setNotification({ show: false, type: '', msg: '' }), 3000);
  };

  const handleShowForm = (type, client = null) => {
    setModalType(type);
    setSelectedClient(client);
    // ✅ Mapping correct des données existantes vers le formulaire
    setForm(client ? { 
        name: client.name, 
        description: client.description || '', 
        logo_url: client.logo || '', 
        website_url: client.websiteUrl || '' 
    } : { name: '', description: '', logo_url: '', website_url: '' });
    setShowFormModal(true);
  };

  const handleShowDelete = (client) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', 'clients');
    try {
      const res = await fetch(`${API_BASE}/api/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const path = await res.text();
      setForm(prev => ({ ...prev, logo_url: path }));
      showNotify('success', 'Image téléchargée !');
    } catch {
      showNotify('error', "Échec de l'upload image");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const method = modalType === 'add' ? 'POST' : 'PUT';
      const url = modalType === 'add' ? `${API_BASE}/api/clients` : `${API_BASE}/api/clients/${selectedClient.id}`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form) // Envoie website_url et logo_url
      });
      if (!res.ok) throw new Error();
      showNotify('success', modalType === 'add' ? 'Client ajouté !' : 'Client mis à jour !');
      fetchClients(currentPage - 1);
      setShowFormModal(false);
    } catch {
      showNotify('error', "Erreur lors de l'enregistrement");
    } finally {
      setModalLoading(false);
    }
  };

  const executeDelete = async () => {
    setModalLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/clients/${selectedClient.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      showNotify('success', 'Client supprimé !');
      if (clients.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        fetchClients(currentPage - 1);
      }
      setShowDeleteModal(false);
    } catch {
      showNotify('error', 'Erreur lors de la suppression');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
            <div>
                <h2>Gestion des Clients</h2>
                <p className="text-muted small">Total : {totalElements} clients enregistrés</p>
            </div>
            <div className="search-wrapper">
                <FiSearch className="search-icon" />
                <input type="text" placeholder="Rechercher par nom..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <button onClick={() => handleShowForm('add')} className="btn-add"><FiPlus /> Nouveau Client</button>
        </div>

        {notification.show && (
            <div className={`toast-sii ${notification.type}`}>
                {notification.type === 'success' ? <FiCheckCircle /> : <FiAlertTriangle />}
                {notification.msg}
            </div>
        )}

        {loading ? (
           <div className="text-center py-5">Chargement...</div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr><th>Logo</th><th>Nom</th><th>Lien Site</th><th className="text-end">Actions</th></tr>
              </thead>
              <tbody>
                {clients.length > 0 ? clients.map(c => (
                  <tr key={c.id}>
                    <td><img src={getFullImageUrl(c.logo, c.name)} alt="logo" className="admin-logo-img" onError={(e) => handleImageError(e, c.name)}/></td>
                    <td className="fw-bold">{c.name}</td>
                    <td>
                        {c.websiteUrl ? (
                            <a href={c.websiteUrl} target="_blank" rel="noreferrer" className="site-link"><FiLink /> Voir le site</a>
                        ) : <span className="text-muted small">Aucun lien</span>}
                    </td>
                    <td className="text-end">
                      <button onClick={() => handleShowForm('edit', c)} className="btn-icon edit"><FiEdit /></button>
                      <button onClick={() => handleShowDelete(c)} className="btn-icon delete"><FiTrash2 /></button>
                    </td>
                  </tr>
                )) : (
                    <tr><td colSpan="4" className="text-center py-4">Aucun client trouvé.</td></tr>
                )}
              </tbody>
            </table>

            <div className="pagination-container">
                <button className="p-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}><FiChevronLeft /> Précédent</button>
                <div className="page-info">Page <strong>{currentPage}</strong> sur {totalPages}</div>
                <button className="p-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>Suivant <FiChevronRight /></button>
            </div>
          </>
        )}

        <Modal isOpen={showFormModal} onClose={() => setShowFormModal(false)} title={modalType === 'add' ? 'Ajouter Client' : 'Modifier Client'}>
          <form onSubmit={handleSubmit} className="admin-form">
            <label className="small fw-bold">Nom du client</label>
            <input name="name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ex: S2I" required className="form-input"/>
            
            <label className="small fw-bold">Description</label>
            <textarea name="description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Courte description..." className="form-input" rows="2"/>
            
            <label className="small fw-bold">URL du site web (Lien cliquable)</label>
            <input name="website_url" value={form.website_url} onChange={e => setForm({...form, website_url: e.target.value})} placeholder="https://www.exemple.com" className="form-input"/>

            <div className="upload-box">
                <label className="small fw-bold">Logo :</label>
                <input type="file" onChange={handleFileChange} accept="image/*" />
            </div>
            {form.logo_url && <img src={getFullImageUrl(form.logo_url, form.name)} alt="prev" className="preview-img" onError={(e) => handleImageError(e, form.name)}/>}
            <button type="submit" className="btn-save" disabled={modalLoading || uploadLoading}>{modalLoading ? 'Enregistrement...' : 'Confirmer'}</button>
          </form>
        </Modal>

        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirmer la suppression">
            <div className="text-center p-3">
                <FiAlertTriangle size={50} color="#ef4444" />
                <h4 className="mt-3">Êtes-vous sûr ?</h4>
                <p>Voulez-vous supprimer <b>{selectedClient?.name}</b> ?</p>
                <div className="d-flex justify-content-center gap-3 mt-4">
                    <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Annuler</button>
                    <button className="btn-confirm-delete" onClick={executeDelete} disabled={modalLoading}>Oui, supprimer</button>
                </div>
            </div>
        </Modal>
      </div>
      <style>{`
        .admin-container { display:flex; min-height:100vh; background:#f4f7fe; }
        .admin-content { flex:1; padding:40px; position:relative; }
        .admin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:30px; gap: 20px; }
        .btn-add { background:#d90000; color:white; border:none; padding:12px 24px; border-radius:10px; cursor:pointer; font-weight:600; display:flex; align-items:center; gap:8px; white-space: nowrap; }
        .admin-table { width:100%; background:white; border-radius:12px; border-collapse:collapse; box-shadow:0 4px 12px rgba(0,0,0,0.05); }
        .admin-table td, .admin-table th { padding:15px; border-bottom:1px solid #f1f5f9; }
        .admin-logo-img { height:40px; width:80px; object-fit:contain; border-radius:5px; background:#f8fafc; }
        .btn-icon { background:none; border:none; cursor:pointer; font-size:18px; margin-left:10px; padding:8px; border-radius:8px; transition:0.2s; }
        .edit { color:#3b82f6; } .edit:hover { background:#eff6ff; }
        .delete { color:#ef4444; } .delete:hover { background:#fef2f2; }
        .site-link { color: #d90000; text-decoration: none; font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; gap: 4px; }
        .pagination-container { display: flex; justify-content: center; align-items: center; gap: 20px; margin-top: 30px; }
        .p-btn { padding: 8px 16px; border-radius: 8px; border: 1px solid #e2e8f0; background: white; cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: 5px; }
        .p-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .page-info { font-size: 0.9rem; color: #4a5568; }
        .search-wrapper { position: relative; flex: 1; max-width: 400px; }
        .search-icon { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-input { width: 100%; padding: 12px 12px 12px 45px; border-radius: 25px; border: 1px solid #e2e8f0; outline: none; }
        .admin-form { display:flex; flex-direction:column; gap:10px; }
        .form-input { padding:10px; border:1px solid #e2e8f0; border-radius:8px; outline:none; }
        .preview-img { height:60px; object-fit:contain; border-radius:8px; align-self:center; margin-top: 5px;}
        .btn-save { background:#d90000; color:white; border:none; padding:12px; border-radius:10px; cursor:pointer; font-weight:700; margin-top:10px; }
        .toast-sii { position:fixed; top:20px; right:20px; padding:15px 25px; border-radius:12px; color:white; z-index:9999; display:flex; align-items:center; gap:10px; box-shadow:0 10px 20px rgba(0,0,0,0.1); }
        .toast-sii.success { background:#10b981; }
        .toast-sii.error { background:#ef4444; }
        .btn-cancel { background:#e2e8f0; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
        .btn-confirm-delete { background:#ef4444; color:white; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
      `}</style>
    </div>
  );
};

export default AdminClients;